import axios, { AxiosError, AxiosHeaders, type AxiosRequestConfig } from "axios";
import { toast } from "sonner";
import {
  clearAuthSessionStorage,
  createAuthSession,
  emitAuthChanged,
  readAuthSession,
  writeAuthSession,
  type AuthSession,
} from "@/app/services/auth-storage";
import type { AuthTokenResponse } from "@/app/types/api";

export type HttpConfig = AxiosRequestConfig & {
  withAuth?: boolean;
  skipErrorToast?: boolean;
  _retry?: boolean;
};

const baseURL = "";
const AUTH_PREFIX = "/api/v1/";
const LOGIN_URL = "/api/v1/auth/login";
const REFRESH_URL = "/api/v1/auth/refresh";
export const ACCESS_TOKEN_REFRESH_LEAD_MS = 5 * 60_000;

export const apiClient = axios.create({
  baseURL,
  timeout: 60_000,
  headers: { "Content-Type": "application/json" },
});

const refreshClient = axios.create({
  baseURL,
  timeout: 60_000,
  headers: { "Content-Type": "application/json" },
});

function shouldAttachAuth(config: AxiosRequestConfig): boolean {
  const url = config.url ?? "";
  return (
    (config as HttpConfig).withAuth !== false &&
    url.startsWith(AUTH_PREFIX) &&
    url !== LOGIN_URL &&
    url !== REFRESH_URL
  );
}

function notifyUnauthorized() {
  clearAuthSessionStorage();
  emitAuthChanged();
  window.dispatchEvent(new CustomEvent("lumina:unauthorized"));
}

let unauthorizedHandled = false;
function handleUnauthorizedToast() {
  if (unauthorizedHandled) return;
  unauthorizedHandled = true;
  toast.error("Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.");
  setTimeout(() => {
    unauthorizedHandled = false;
  }, 500);
}

let refreshPromise: Promise<AuthSession> | null = null;
function needsRefresh(session: AuthSession, minValidityMs: number): boolean {
  return session.accessTokenExpiresAt - Date.now() <= minValidityMs;
}

async function runRefreshSession(): Promise<AuthSession> {
  const current = readAuthSession();
  if (!current?.refreshToken) {
    throw new Error("No refresh token available");
  }
  const { data } = await refreshClient.post<AuthTokenResponse>(
    REFRESH_URL,
    null,
    {
      headers: {
        Authorization: `Bearer ${current.refreshToken}`,
      },
    }
  );
  const next = createAuthSession(data);
  writeAuthSession(next);
  emitAuthChanged();
  return next;
}

async function refreshSession(): Promise<AuthSession> {
  refreshPromise ??= runRefreshSession().finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}

export async function ensureFreshSession(
  minValidityMs = ACCESS_TOKEN_REFRESH_LEAD_MS
): Promise<AuthSession | null> {
  const current = readAuthSession();
  if (!current) return null;
  if (!needsRefresh(current, minValidityMs)) return current;
  try {
    return await refreshSession();
  } catch (error) {
    notifyUnauthorized();
    handleUnauthorizedToast();
    throw error;
  }
}

apiClient.interceptors.request.use(async (config) => {
  if (!shouldAttachAuth(config)) {
    return config;
  }

  try {
    const current = await ensureFreshSession();
    if (current?.accessToken) {
      const headers = new AxiosHeaders(config.headers as any);
      headers.set("Authorization", `Bearer ${current.accessToken}`);
      config.headers = headers;
    }
    return config;
  } catch (error) {
    return Promise.reject(error);
  }
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = (error.config ?? {}) as HttpConfig;
    const url = original.url ?? "";

    if (error.response?.status !== 401) {
      return Promise.reject(error);
    }

    if (url === LOGIN_URL) {
      return Promise.reject(error);
    }

    if (url === REFRESH_URL || original.withAuth === false || original._retry) {
      notifyUnauthorized();
      handleUnauthorizedToast();
      return Promise.reject(error);
    }

    const session = readAuthSession();
    if (!session?.refreshToken) {
      notifyUnauthorized();
      handleUnauthorizedToast();
      return Promise.reject(error);
    }

    try {
      const next = await refreshSession();
      original._retry = true;
      const headers = new AxiosHeaders(original.headers as any);
      headers.set("Authorization", `Bearer ${next.accessToken}`);
      original.headers = headers;
      return apiClient.request(original);
    } catch (refreshError) {
      notifyUnauthorized();
      handleUnauthorizedToast();
      return Promise.reject(refreshError);
    }
  }
);

export const http = {
  get: async <T>(url: string, params?: unknown, config?: HttpConfig) => {
    const res = await apiClient.get<T>(url, { params, ...config });
    return res.data;
  },
  post: async <T, B = unknown>(url: string, body?: B, config?: HttpConfig) => {
    const res = await apiClient.post<T>(url, body, config);
    return res.data;
  },
  patch: async <T, B = unknown>(url: string, body?: B, config?: HttpConfig) => {
    const res = await apiClient.patch<T>(url, body, config);
    return res.data;
  },
  put: async <T, B = unknown>(url: string, body?: B, config?: HttpConfig) => {
    const res = await apiClient.put<T>(url, body, config);
    return res.data;
  },
  delete: async <T>(url: string, config?: HttpConfig) => {
    const res = await apiClient.delete<T>(url, config);
    return res.data;
  },
};

export function parseApiError(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { detail?: unknown; message?: unknown } | undefined;
    const m = data?.detail ?? data?.message;
    if (typeof m === "string") return m;
    if (m) return JSON.stringify(m);
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return String(err);
}
